import localeResource from "@root/resources/locales.json";

const localeCatalog = localeResource.locales;

export type Locale = keyof typeof localeCatalog;
export const defaultLocale = getDefaultLocale();
export type MessageKey =
  keyof (typeof localeCatalog)[typeof defaultLocale]["messages"];
export interface LocaleMetadata {
  code: Locale;
  label: string;
  path: string;
  languageTag: string;
  languageAliases: string[];
  page: {
    applicationName: string;
    title: string;
    description: string;
    openGraphSiteName: string;
    openGraphLocale: string;
  };
  manifestPath: string;
}

type Messages = Record<MessageKey, string>;

function getDefaultLocale(): Locale {
  if (localeResource.defaultLocale in localeCatalog)
    return localeResource.defaultLocale as Locale;
  throw new Error(`Unknown default locale: ${localeResource.defaultLocale}`);
}

export const localeMetadata: LocaleMetadata[] = (
  Object.keys(localeCatalog) as Locale[]
).map((code) => {
  const { label, path, languageTag, languageAliases, page, manifest } =
    localeCatalog[code];
  return {
    code,
    label,
    path,
    languageTag,
    languageAliases,
    page,
    manifestPath: manifest.path,
  };
});

export const messages = Object.fromEntries(
  (Object.keys(localeCatalog) as Locale[]).map((locale) => [
    locale,
    localeCatalog[locale].messages,
  ]),
) as Record<Locale, Messages>;

export function getLocaleFromLanguageTag(languageTag: string): Locale {
  const normalizedTag = languageTag.toLowerCase();
  return (
    localeMetadata.find(
      ({ languageTag, languageAliases }) =>
        normalizedTag === languageTag.toLowerCase() ||
        languageAliases.some((tag) => normalizedTag === tag.toLowerCase()),
    )?.code ?? defaultLocale
  );
}
